// The workspace switcher's default-workspace star, shared by the dashboard
// pages (through useAnalyticsNavActions) and DashAI. Each caller passes its own
// notification function, so a failed save shows in that caller's UI library.

import { useCallback } from "react";

import { useAppDispatch, useAppSelector } from "../store/hooks";
import {
  DEFAULT_WORKSPACE_SAVE_ERROR,
  selectEffectiveDefaultWorkspace,
  setDefaultWorkspace,
} from "../slices/authSlice";

export function useDefaultWorkspaceStar(notifyError: (message: string) => void) {
  const dispatch = useAppDispatch();
  const defaultWorkspace: string | null = useAppSelector(selectEffectiveDefaultWorkspace);
  const savingDefaultWorkspace = useAppSelector((state) =>
    Boolean(state.auth.defaultWorkspaceSaving)
  );

  // The thunk applies the new default at once and rolls it back on failure,
  // so the only thing left here is telling the user a save failed. A save the
  // thunk's condition skipped is not a failure.
  const onSetDefaultWorkspace = useCallback(
    async (client: string) => {
      const result = await dispatch(setDefaultWorkspace(client));
      if (setDefaultWorkspace.rejected.match(result) && !result.meta.condition) {
        notifyError(DEFAULT_WORKSPACE_SAVE_ERROR);
      }
    },
    [dispatch, notifyError]
  );

  return { defaultWorkspace, savingDefaultWorkspace, onSetDefaultWorkspace };
}
